// ABOUTME: Helpers of the `link` additional info of a link field: which fields are links and the ids a value holds.
// ABOUTME: A link field stores one node id, or an array of ids when `multiValue`.

import { AdditionalInfoFieldType, AdditionalInfoType } from '@src/__generated__/types';

export type LinkInfoField = {
    fieldCode: string;
    fieldName: string;
    hierarchy: string;
    multiValue: boolean;
    linkedByRowNode: boolean;
    readonly: boolean;
};

type AdditionalInfoLike = Pick<
    AdditionalInfoType,
    | 'fieldCode'
    | 'fieldName'
    | 'fieldType'
    | 'hierarchy'
    | 'multiValue'
    | 'linkedByRowNode'
    | 'readonly'
    | 'showOnLinkTable'
>;

/** The `link` fields shown on the link table, with the settings the picker needs. */
export const linkInfoFieldsOf = (
    additionalInfo: ReadonlyArray<AdditionalInfoLike | null | undefined> | null | undefined,
): LinkInfoField[] =>
    (additionalInfo ?? []).flatMap(info =>
        info?.fieldType === AdditionalInfoFieldType.Link &&
        info.fieldCode &&
        info.hierarchy &&
        info.showOnLinkTable
            ? [
                  {
                      fieldCode: info.fieldCode,
                      fieldName: info.fieldName || info.fieldCode,
                      hierarchy: info.hierarchy,
                      multiValue: !!info.multiValue,
                      linkedByRowNode: !!info.linkedByRowNode,
                      readonly: !!info.readonly,
                  },
              ]
            : [],
    );

/** The additional info the data table renders itself: every field but the `link` ones. */
export const withoutLinkInfo = <T extends { fieldType?: AdditionalInfoFieldType | null }>(
    additionalInfo: ReadonlyArray<T | null | undefined> | null | undefined,
): (T | null | undefined)[] =>
    (additionalInfo ?? []).filter(info => info?.fieldType !== AdditionalInfoFieldType.Link);

/**
 * The node ids a `link` value holds: one id, an array of ids (as stored), or the JSON array the
 * link table keeps while editing (its extraInfo values are strings).
 */
export const linkInfoIds = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.filter((id): id is string => typeof id === 'string' && id !== '');
    }
    if (typeof value !== 'string' || value === '') {
        return [];
    }
    if (value.startsWith('[')) {
        try {
            return linkInfoIds(JSON.parse(value));
        } catch {
            return [value];
        }
    }
    return [value];
};

/** The value the link table keeps for picked ids: the id, or a JSON array for a multi-value field. */
export const encodeLinkInfoIds = (ids: string[], multiValue: boolean): string =>
    multiValue ? JSON.stringify(ids) : ids[0] ?? '';

/**
 * The link entries to store: each multi-value `link` field the table kept as a JSON array goes back
 * to an array of ids, the shape the server validates and the readers expect.
 */
export const decodeLinkInfoEntries = (
    entries: ReadonlyArray<Record<string, unknown> | string>,
    fields: LinkInfoField[],
): (Record<string, unknown> | string)[] =>
    entries.map(entry =>
        typeof entry === 'string'
            ? entry
            : fields.reduce<Record<string, unknown>>(
                  (decoded, field) =>
                      field.multiValue && field.fieldCode in decoded
                          ? { ...decoded, [field.fieldCode]: linkInfoIds(decoded[field.fieldCode]) }
                          : decoded,
                  entry,
              ),
    );
